import React, { useEffect, useState } from "react";
import { useNavigate} from "react-router-dom";
import {
  ref,
  set,
  get,
  onValue,
  update,
  remove,
  push,
  onDisconnect,
} from "firebase/database";
import { database } from "../../database/firebase";
import Swal from "sweetalert2";
import "./tictac.css";
import { sendReport } from "../functions/sendReport";

function TicTac({ updateHeader }) {
  const [userData, setUserData] = useState(null);
  const [game, setGame] = useState(null);
  const [symbol, setSymbol] = useState(null);
  const [stage, setStage] = useState("idle");
  const [availableCount, setAvailableCount] = useState(0);
  const TURN_TIME = 20;
  const [remaining, setRemaining] = useState(TURN_TIME);
  const [hasJoined, setHasJoined] = useState(false);
  const [mode, setMode] = useState(null);
  const navigate = useNavigate();
  const computerStartRef = React.useRef(null);
  // idle | waiting | playing | finished

  /* ---------------- Load user ---------------- */
  useEffect(() => {
    const stored = sessionStorage.getItem("userData");
    if (stored) setUserData(JSON.parse(stored));
    if(stored) {
       updateHeader({
                    backButtonRedirect: "/login?save=true", // New Back Button Target
        });
    }
  }, []);

  /* ---------------- Join Game ---------------- */
  const joinGame = async () => {
    if (!userData) return;
    setMode("online");
    const playerRef = ref(
      database,
      `/${userData.sessionId}/joinedPlayers/${userData.userId}`,
    );

    await set(playerRef, {
      userId: userData.userId,
      name: userData.name,
      joinedAt: Date.now(),
    });

    setHasJoined(true);
    setStage("waiting");
    await matchPlayers();
  };

  const showResult = (data) => {
    let title = "";
    let icon = "info";
    let points = 20;

        if (data.winner === "draw") {
          title = "It's a Draw 🤝";
          icon = "info";
          points = 10;
        } else if (data.winner === symbol) {
          title = "You Won 🎉";
          icon = "success";
          points = 0;
        } else {
          title = "You Lost 😢";
          icon = "error";
          points = 0;
        }

        const reportData = {
            sessionId: userData.sessionId,
            role: userData.role,
            token: userData.token || null,
            name: userData.name,
            userId: userData.userId,
            gameId: userData.gameId || "",
            organizationId: userData.organizationId,
            points: 20,
            time: mode === "computer" ? getComputerTimeSpent() : "00:00:00",
            reportId: userData.reportId || null,
            ans: "",
          };

          console.log(reportData);

           sendReport(reportData)
            .then((apiRes) => {
              console.log("✔ Report API SUCCESS:", apiRes);
            })
            .catch((err) => {
              console.error("❌ Report API FAILED:", err);
            })
            .finally(() => {
                const key = "report-" + userData.sessionId;

                  localStorage.setItem(
                    key,
                    JSON.stringify({
                      sessionId: userData.sessionId,
                      userId: userData.userId,
                      time: reportData.time,
                      points: reportData.points,
                      createdAt: Date.now(),
                    })
                  );

                  Swal.close();
                  navigate("/thankyou");
            });

    return Swal.fire({
      title,
      icon,
      confirmButtonText: "OK",
      allowOutsideClick: false,
    });
  };


    useEffect(() => {
    if (!userData) return;

    const key = "report-" + userData.sessionId;
    if (localStorage.getItem(key)) {
      navigate("/thankyou");
    }
  }, [userData]);

  useEffect(() => {
    if (!userData) return;

    const joinedRef = ref(database, `/${userData.sessionId}/joinedPlayers`);
    const gameRef = ref(database, `/${userData.sessionId}/activeGame`);

    const unsubscribe = onValue(joinedRef, async (snap) => {
      if (!snap.exists()) {
        setAvailableCount(0);
        return;
      }

      let players = Object.keys(snap.val());

      // ❌ remove self
      players = players.filter((id) => id !== userData.userId);

      // ❌ remove players who are currently playing
      const gameSnap = await get(gameRef);
      if (gameSnap.exists()) {
        const playing = Object.values(gameSnap.val().players || {});
        players = players.filter((id) => !playing.includes(id));
      }

      setAvailableCount(players.length);
    });

    return () => unsubscribe();
  }, [userData]);

  /* ---------------- Matchmaking ---------------- */
  const matchPlayers = async () => {
    const joinedRef = ref(database, `/${userData.sessionId}/joinedPlayers`);
    const gameRef = ref(database, `/${userData.sessionId}/activeGame`);

    const gameSnap = await get(gameRef);
    if (gameSnap.exists()) return; // game already running

    const playersSnap = await get(joinedRef);
    if (!playersSnap.exists()) return;

    const allPlayers = Object.keys(playersSnap.val());

    // need at least 2 opted-in players
    if (allPlayers.length < 2) return;

    const [p1, p2] = allPlayers;

    await set(gameRef, {
      players: { X: p1, O: p2 },
      board: Array(9).fill(""),
      turn: "X",
      turnStartedAt: Date.now(),
      status: "playing",
      winner: null,
      winType: null,
    });
  };

  /* ---------------- Listen Game ---------------- */
  useEffect(() => {
    if (!userData) return;

    const gameRef = ref(database, `/${userData.sessionId}/activeGame`);

    const unsubscribe = onValue(gameRef, async (snap) => {
      if (!snap.exists()) {
        setGame(null);
        setSymbol(null);

        if (hasJoined) {
          setStage("waiting");
        } else {
          setStage("idle");
        }
        return;
      }
      const data = snap.val();

      const isPlayer =
        data.players?.X === userData.userId ||
        data.players?.O === userData.userId;

      if (!isPlayer) {
        // not your game → stay waiting
        setGame(null);
        setSymbol(null);
        setStage("waiting");
        return;
      }

      if (data.status === "playing") {
        // active game
        setGame(data);
        setStage("playing");

        if (data.players.X === userData.userId) setSymbol("X");
        if (data.players.O === userData.userId) setSymbol("O");
        return;
      }

      // 🔥 GAME ENDED → FREE PLAYER IMMEDIATELY
      if (data.status === "finished") {
        // 🔔 SHOW RESULT FIRST
        await showResult(data);

        const gameRef = ref(database, `/${userData.sessionId}/activeGame`);
        const snap = await get(gameRef);

        if (snap.exists()) {
          const gameData = snap.val();

          const updates = {};
          updates[
            `/${userData.sessionId}/joinedPlayers/${gameData.players.X}`
          ] = null;
          updates[
            `/${userData.sessionId}/joinedPlayers/${gameData.players.O}`
          ] = null;
          updates[`/${userData.sessionId}/activeGame`] = null;

          await update(ref(database), updates);
        }

        // 🧹 local reset
        setHasJoined(false);
        setGame(null);
        setSymbol(null);
        setStage("idle");
        return;
      }
    });

    return () => unsubscribe();
  }, [userData, hasJoined]);

  const getComputerTimeSpent = () => {
  if (!computerStartRef.current) return "00:00:00";

  const seconds = Math.min(
    Math.floor((Date.now() - computerStartRef.current) / 1000),
    20
  );

  return `00:00:${String(seconds).padStart(2, "0")}`;
};

  const startComputerGame = () => {
    setMode("computer");
    setSymbol("X");
    setRemaining(TURN_TIME);

    computerStartRef.current = Date.now();

    setGame({
      board: Array(9).fill(""),
      turn: "X",
      status: "playing",
      turnStartedAt: Date.now(),
    });

    setStage("playing");
  };

  /* ---------------- Save Result ---------------- */
  const saveResult = async (data) => {
    const historyRef = push(
      ref(database, `/${userData.sessionId}/gameHistory`),
    );

    await set(historyRef, {
      X: data.players.X,
      O: data.players.O,
      winner: data.winner,
      endedAt: Date.now(),
    });
  };

  /* ---------------- Move ---------------- */
  const makeMove = async (index) => {
    if (!game || game.board[index] !== "") return;
    if (game.turn !== symbol) return;

    const board = [...game.board];
    board[index] = symbol;

    const winner = checkWinner(board);

    // 🧠 COMPUTER MODE
    if (mode === "computer") {
      if (winner) {
        await showResult({ winner });
        goToIdle();
        return;
      }

      // computer move
      const computerBoard = computerMove(board);
      const computerWinner = checkWinner(computerBoard);

      if (computerWinner) {
        await showResult({ winner: computerWinner });
        goToIdle();
        return;
      }

      setGame({
        board: computerBoard,
        turn: "X",
        status: "playing",
        turnStartedAt: Date.now(),
      });

      return;
    }

    // 🌐 ONLINE MODE (unchanged logic)
    const updates = {
      board,
      turn: symbol === "X" ? "O" : "X",
      turnStartedAt: Date.now(),
    };

    if (winner) {
      updates.status = "finished";
      updates.winner = winner;
    }

    await update(ref(database, `/${userData.sessionId}/activeGame`), updates);
  };

  /* ---------------- Win Logic ---------------- */
  const checkWinner = (b) => {
    const lines = [
      [0, 1, 2],
      [3, 4, 5],
      [6, 7, 8],
      [0, 3, 6],
      [1, 4, 7],
      [2, 5, 8],
      [0, 4, 8],
      [2, 4, 6],
    ];
    for (let [a, b1, c] of lines) {
      if (b[a] && b[a] === b[b1] && b[a] === b[c]) return b[a];
    }
    if (!b.includes("")) return "draw";
    return null;
  };

  const computerMove = (board) => {
    const empty = board
      .map((v, i) => (v === "" ? i : null))
      .filter((v) => v !== null);

    const move = empty[Math.floor(Math.random() * empty.length)];

    const newBoard = [...board];
    newBoard[move] = "O";
    return newBoard;
  };

  const leaveLobby = async () => {
    if (!userData) return;

    const playerRef = ref(
      database,
      `/${userData.sessionId}/joinedPlayers/${userData.userId}`,
    );

    await remove(playerRef);

    setHasJoined(false);
    setStage("idle");
    setGame(null);
    setSymbol(null);
  };

  /* ---------------- Reset ---------------- */
  const resetGame = async () => {
    // 1️⃣ clear local game state
    setGame(null);
    setSymbol(null);
    setStage("idle");

    // 2️⃣ remove active game ONLY (free both players)
    await remove(ref(database, `/${userData.sessionId}/activeGame`));

    // 3️⃣ re-enter availability (presence)
    const playerRef = ref(
      database,
      `/${userData.sessionId}/joinedPlayers/${userData.userId}`,
    );

    await set(playerRef, {
      userId: userData.userId,
      name: userData.name,
      joinedAt: Date.now(),
    });

    // 4️⃣ try matchmaking again
    // matchPlayers();
  };

  useEffect(() => {
    if (!game || game.status !== "playing") return;

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - game.turnStartedAt) / 1000);

      const left = TURN_TIME - elapsed;
      setRemaining(left > 0 ? left : 0);

      // 🔥 Timeout check (authoritative)
      if (left <= 0) {
        handleTimeout();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [game]);

  const goToIdle = () => {
    setGame(null);
    setSymbol(null);
    setMode(null);
    setStage("idle");
  };

  const handleTimeout = async () => {
    if (!game || game.status !== "playing") return;

    const loser = game.turn;
    const winner = loser === "X" ? "O" : "X";

    // 🧠 COMPUTER MODE
    if (mode === "computer") {
      await showResult({ winner });
      goToIdle();
      return;
    }

    // 🌐 ONLINE MODE (unchanged)
    const gameRef = ref(database, `/${userData.sessionId}/activeGame`);
    const snap = await get(gameRef);
    if (!snap.exists()) return;

    const current = snap.val();
    if (current.status !== "playing") return;

    await update(gameRef, {
      status: "finished",
      winner,
      winType: "timeout",
    });
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="tt-wrapper">
      <div className="tt-card">
        {stage === "idle" && (
          <>
            <img src="/img/tic_tac_logo.png" className="game-logo" />

            <p className="player-count">
              Available players: <strong>{availableCount}</strong>
            </p>

            <button className="tt-btn" onClick={joinGame} disabled={!userData}>
              Join Game
            </button>
            <br></br>
            <button className="tt-btn secondary" onClick={startComputerGame}>
              Play with Computer
            </button>
          </>
        )}

        {stage === "waiting" && (
          <>
            <h3>Waiting for opponent…</h3>
            <p className="player-count">
              Players in lobby: <strong>{availableCount}</strong>
            </p>
          </>
        )}

        {stage === "playing" && (
          <>
            <h3>
              You are <span>{symbol}</span>
            </h3>
            <div className="board">
              {game.board.map((cell, i) => (
                <div
                  key={i}
                  className={`cell ${cell}`}
                  onClick={() => makeMove(i)}
                >
                  {cell}
                </div>
              ))}
            </div>
            <div className="turn-box">
              {game.turn === symbol ? (
                <span className="your-turn">Your turn</span>
              ) : (
                <span className="their-turn">Opponent's turn</span>
              )}

              <div className={`timer ${remaining <= 5 ? "danger" : ""}`}>
                ⏱ {remaining}s
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TicTac;
